import React, { createContext, useContext, useReducer, ReactNode, useEffect } from "react";

interface Permission {
  create: boolean;
  read: boolean;
  update: boolean;
  delete: boolean;
}

interface RolePermissions {
  [role: string]: {
    [permissionName: string]: Permission;
  };
}

interface Account {
  id?: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: string[];
  permissions: RolePermissions;
  project: number;
  application:number;
  logo?: string;
}

interface User {
  roles: string[];
  permissions: RolePermissions;
}

interface AuthState {
  isLoggedIn: boolean;
  account: Account | null;
  userData: User | null;
}
// Action types for authentication
type AuthAction =
  | { type: "LOGIN"; payload: { account: Account } }
  | { type: "LOGOUT" }
  | { type: "USER"; payload: { userData: User } };

interface AuthContextType {
  user: AuthState;
  dispatch: React.Dispatch<AuthAction>;
}
// Creating AuthContext with undefined as default value
const AuthContext = createContext<AuthContextType | undefined>(undefined);
// Custom hook to access the AuthContext
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
// Reducer function to handle authentication actions
const reducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case "LOGIN":
      localStorage.setItem("user_account", JSON.stringify(action.payload.account));
      return { ...state, isLoggedIn: true, account: action.payload.account };

    case "LOGOUT":
      localStorage.removeItem("user_account");
      localStorage.removeItem("access_token");
      return { isLoggedIn: false, account: null, userData: null };

    case "USER":
      return { ...state, userData: action.payload.userData };

    default:
      return state;
  }
};
// AuthProvider component to provide the authentication context to the app
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Check for stored user data in localStorage and initialize state accordingly
  const storedUser = localStorage.getItem("user_account");
  const initialState: AuthState = storedUser
    ? { isLoggedIn: true, account: JSON.parse(storedUser), userData: null }
    : { isLoggedIn: false, account: null, userData: null };

  const [user, dispatch] = useReducer(reducer, initialState);

  // Effect to handle login when user data is available in localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem("user_account");
    if (storedUser) {
      dispatch({ type: "LOGIN", payload: { account: JSON.parse(storedUser) } });
    }
  }, []);

  return <AuthContext.Provider value={{ user, dispatch }}>{children}</AuthContext.Provider>;
};
